<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { api } from '@/api/client'
import { Badge } from '@kyoto2/components/ui/badge'
import type { ResourceRef } from '@/lib/resource-links'
import { cachedPreviewFetch } from '@/lib/preview-cache'

const props = defineProps<{ resourceRef: ResourceRef }>()

const loading = ref(true)
const task = ref<{ name: string; description?: string | null; status?: string } | null>(null)
let cancelled = false
const cacheKey = `task:${props.resourceRef.id}`

onMounted(async () => {
  try {
    task.value = await cachedPreviewFetch(cacheKey, () =>
      api.get(`/tasks/${Number(props.resourceRef.id)}`),
    )
  } catch {
    task.value = null
  } finally {
    if (!cancelled) loading.value = false
  }
})

onUnmounted(() => { cancelled = true })
</script>

<template>
  <div class="p-2 text-xs space-y-1 max-w-[260px] min-w-0">
    <Badge variant="outline" class="text-[10px]">task</Badge>
    <p v-if="loading" class="text-muted-foreground">Loading…</p>
    <template v-else-if="task">
      <p class="font-medium break-words">{{ task.name }}</p>
      <p v-if="task.description" class="text-muted-foreground line-clamp-2 break-words">{{ task.description }}</p>
      <Badge v-if="task.status" variant="secondary" class="text-[10px]">{{ task.status }}</Badge>
    </template>
    <p v-else class="text-muted-foreground">Task {{ resourceRef.id }}</p>
  </div>
</template>
